import {
  Briefcase,
  Building2,
  Clock,
  type LucideIcon,
  Megaphone,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatePickerInput } from "@/components/date-picker-input";
import { FilterBuilder } from "@/components/filter-builder";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccountFilterState } from "@/hooks/use-account-filter-state";
import { useAvailableFilterFields } from "@/hooks/use-available-filter-fields";
import { useCampaignFilterState } from "@/hooks/use-campaign-filter-state";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { useOpportunityFilterState } from "@/hooks/use-opportunity-filter-state";
import { usePersonFilterState } from "@/hooks/use-person-filter-state";
import { useSalespersonFilterState } from "@/hooks/use-salesperson-filter-state";
import { useWebpageFilterState } from "@/hooks/use-webpage-filter-state";
import { formatDateToISO, safeParseDateString } from "@/lib/date-utils";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP, hasIncompleteFilters } from "@/lib/filters";

interface EntityFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: string;
  entity:
    | "campaign"
    | "channel"
    | "salesperson"
    | "webpage"
    | "person"
    | "opportunity"
    | "account";
  title?: string;
  hideCampaignFilters?: boolean;
}

type MetricCategory = "Account" | "Opportunity" | "Touchpoint" | "Campaign";

function CategoryPill({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: MetricCategory;
}) {
  const variant = `category-${label.toLowerCase()}` as BadgeProps["variant"];
  return (
    <Badge
      variant={variant}
      startIcon={<Icon className="h-3 w-3" />}
      className="px-2.5 py-0.5 text-xs"
    >
      {label}
    </Badge>
  );
}

function FilterSection({
  children,
  category,
  icon,
}: {
  children: React.ReactNode;
  category: MetricCategory;
  icon: LucideIcon;
}) {
  return (
    <div className="grid grid-cols-[6rem,1fr] gap-8 items-start border-b border-border py-4 last:border-0 last:pb-0 first:pt-0">
      <div className="relative flex items-center">
        <div className="shrink-0">
          <CategoryPill icon={icon} label={category} />
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

export function EntityFilterModal({
  open,
  onOpenChange,
  scope,
  entity,
  title,
  hideCampaignFilters = false,
}: EntityFilterModalProps) {
  // ---------------------------------------------------------------------------
  // Fetch available field metadata (standard + custom) per object
  // ---------------------------------------------------------------------------

  const { fields: accountFields, isLoading: loadingAccount } =
    useAvailableFilterFields("account");

  const { fields: opportunityFields, isLoading: loadingOpportunity } =
    useAvailableFilterFields("opportunity");

  const { fields: campaignFields, isLoading: loadingCampaign } =
    useAvailableFilterFields("campaign");

  const { fields: touchpointFields, isLoading: loadingTouchpoint } =
    useAvailableFilterFields("touchpoint");

  // ---------------------------------------------------------------------------
  // Persistent filter state for this modal scope - use appropriate hook based on entity
  // ---------------------------------------------------------------------------

  const accountFilterState = useAccountFilterState(scope);
  const campaignFilterState = useCampaignFilterState(scope);
  const channelFilterState = useChannelFilterState(scope);
  const opportunityFilterState = useOpportunityFilterState(scope);
  const personFilterState = usePersonFilterState(scope);
  const salespersonFilterState = useSalespersonFilterState(scope);
  const webpageFilterState = useWebpageFilterState(scope);

  // Select the appropriate filter state based on entity type
  const filterStateMap = {
    account: accountFilterState,
    campaign: campaignFilterState,
    channel: channelFilterState,
    opportunity: opportunityFilterState,
    person: personFilterState,
    salesperson: salespersonFilterState,
    webpage: webpageFilterState,
  };
  const filterState = filterStateMap[entity];

  // ---------------------------------------------------------------------------
  // Filter Extraction with Type Safety
  // ---------------------------------------------------------------------------
  // Different entity types have different available filter types:
  // - 'account' entity cannot filter by accounts (no accountFilters)
  // - 'opportunity' entity cannot filter by opportunities (no opportunityFilters)
  // - Not all entities support all filter types (e.g., campaignFilters only for channels)
  //
  // This conditional logic safely extracts filters based on entity capabilities,
  // using runtime checks to handle the varying filter state shapes.
  //
  // Future improvement: Consider using discriminated union types for better
  // compile-time type safety and to eliminate these runtime checks.
  // ---------------------------------------------------------------------------

  // Account filters exist for entities that can filter by account (not account itself)
  const accountFilters =
    entity === "account"
      ? EMPTY_GROUP
      : "accountFilters" in filterState
        ? filterState.accountFilters
        : EMPTY_GROUP;
  const setAccountFilters =
    entity === "account"
      ? () => {}
      : "setAccountFilters" in filterState
        ? filterState.setAccountFilters
        : () => {};

  // Opportunity filters exist for entities that can filter by opportunity (not opportunity itself)
  const opportunityFilters =
    entity === "opportunity"
      ? EMPTY_GROUP
      : "opportunityFilters" in filterState
        ? filterState.opportunityFilters
        : EMPTY_GROUP;
  const setOpportunityFilters =
    entity === "opportunity"
      ? () => {}
      : "setOpportunityFilters" in filterState
        ? filterState.setOpportunityFilters
        : () => {};

  // Touchpoint filters exist for specific entities that support touchpoint filtering
  // Map each entity type to its corresponding filter state for touchpoint filters
  const touchpointFilterStateMap = {
    account: accountFilterState,
    campaign: campaignFilterState,
    channel: channelFilterState,
    opportunity: opportunityFilterState,
    person: personFilterState,
    salesperson: salespersonFilterState,
    webpage: webpageFilterState,
  };

  const touchpointFilterState = touchpointFilterStateMap[entity];
  const touchpointFilters =
    "touchpointFilters" in touchpointFilterState
      ? touchpointFilterState.touchpointFilters
      : EMPTY_GROUP;
  const setTouchpointFilters =
    "setTouchpointFilters" in touchpointFilterState
      ? touchpointFilterState.setTouchpointFilters
      : () => {};

  // Campaign filters only exist for channel entity (unless explicitly hidden)
  const campaignFilters =
    entity === "channel" && !hideCampaignFilters
      ? (filterState as typeof channelFilterState).campaignFilters
      : EMPTY_GROUP;
  const setCampaignFilters =
    entity === "channel" && !hideCampaignFilters
      ? (filterState as typeof channelFilterState).setCampaignFilters
      : () => {};

  // ---------------------------------------------------------------------------
  // Local staged state – only committed when the user clicks "Apply"
  // ---------------------------------------------------------------------------

  const [stagedAccountFilters, setStagedAccountFilters] =
    useState<FilterGroup>(accountFilters);
  const [stagedOpportunityFilters, setStagedOpportunityFilters] =
    useState<FilterGroup>(opportunityFilters);
  const [stagedTouchpointFilters, setStagedTouchpointFilters] =
    useState<FilterGroup>(touchpointFilters);
  const [stagedCampaignFilters, setStagedCampaignFilters] =
    useState<FilterGroup>(campaignFilters);

  // Reset the staged filters whenever the dialog is opened so we always start
  // from the currently persisted state.
  useEffect(() => {
    if (open) {
      setStagedAccountFilters(accountFilters);
      setStagedOpportunityFilters(opportunityFilters);
      setStagedTouchpointFilters(touchpointFilters);
      setStagedCampaignFilters(campaignFilters);
    }
  }, [
    open,
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
  ]);

  const handleClearAll = () => {
    setStagedAccountFilters(EMPTY_GROUP);
    setStagedOpportunityFilters(EMPTY_GROUP);
    setStagedTouchpointFilters(EMPTY_GROUP);
    setStagedCampaignFilters(EMPTY_GROUP);
  };

  const handleApply = () => {
    // Validate all filter groups for incomplete filters
    if (
      hasIncompleteFilters(stagedAccountFilters) ||
      hasIncompleteFilters(stagedOpportunityFilters) ||
      hasIncompleteFilters(stagedTouchpointFilters) ||
      (entity === "channel" &&
        !hideCampaignFilters &&
        hasIncompleteFilters(stagedCampaignFilters))
    ) {
      toast.error(
        "Cannot apply filters – please fill out all required filter values first.",
      );
      return;
    }

    setAccountFilters(stagedAccountFilters);
    setOpportunityFilters(stagedOpportunityFilters);
    setTouchpointFilters(stagedTouchpointFilters);

    if (entity === "channel" && !hideCampaignFilters) {
      setCampaignFilters(stagedCampaignFilters);
    }
    onOpenChange(false);
  };

  // Helper to handle date range filter from touchpoint section
  const handleTouchpointDateChange = (
    type: "from" | "to",
    date: Date | undefined,
  ) => {
    const value = formatDateToISO(date);

    const currentFilters = stagedTouchpointFilters.filters;

    // Remove existing timestamp filters
    const nonTimestampFilters = currentFilters.filter(
      (f) => !("field" in f) || f.field !== "timestamp",
    );

    // Get current values
    const fromFilter = currentFilters.find(
      (f) =>
        "field" in f && f.field === "timestamp" && "op" in f && f.op === "GTE",
    );
    const toFilter = currentFilters.find(
      (f) =>
        "field" in f && f.field === "timestamp" && "op" in f && f.op === "LTE",
    );

    const currentFrom =
      fromFilter && "values" in fromFilter && Array.isArray(fromFilter.values)
        ? (fromFilter.values[0] as string)
        : "";
    const currentTo =
      toFilter && "values" in toFilter && Array.isArray(toFilter.values)
        ? (toFilter.values[0] as string)
        : "";

    // Determine new values
    const newFrom = type === "from" ? value : currentFrom;
    const newTo = type === "to" ? value : currentTo;

    // Build new filter list
    const newFilters = [...nonTimestampFilters];

    // Add GTE filter if from date is provided
    if (newFrom) {
      newFilters.push({
        field: "timestamp",
        op: "GTE",
        values: [newFrom],
      });
    }

    // Add LTE filter if to date is provided
    if (newTo) {
      newFilters.push({
        field: "timestamp",
        op: "LTE",
        values: [newTo],
      });
    }

    setStagedTouchpointFilters({
      ...stagedTouchpointFilters,
      filters: newFilters,
    });
  };

  // Get current touchpoint date values for date picker inputs
  const fromFilter = stagedTouchpointFilters.filters.find(
    (f) =>
      "field" in f && f.field === "timestamp" && "op" in f && f.op === "GTE",
  );
  const toFilter = stagedTouchpointFilters.filters.find(
    (f) =>
      "field" in f && f.field === "timestamp" && "op" in f && f.op === "LTE",
  );

  const touchpointDateFrom =
    fromFilter && "values" in fromFilter && Array.isArray(fromFilter.values)
      ? (fromFilter.values[0] as string)
      : "";
  const touchpointDateTo =
    toFilter && "values" in toFilter && Array.isArray(toFilter.values)
      ? (toFilter.values[0] as string)
      : "";

  // Use provided title or default based on entity type
  // Account/opportunity/person use "related objects" phrasing, others use entity-specific metrics phrasing
  const dialogTitle =
    title ??
    (entity === "account" || entity === "opportunity" || entity === "person"
      ? "Filter by related objects"
      : "Configure filters for top-level metrics");

  // Determine touchpoint UI mode based on entity type
  // Account/opportunity/person entities use FilterBuilder for full field flexibility
  // Campaign/channel/salesperson/webpage entities use date picker for timestamp-only filtering
  const usesFilterBuilderForTouchpoints =
    entity === "account" || entity === "opportunity" || entity === "person";
  const usesDatePickerForTouchpoints =
    entity === "campaign" ||
    entity === "channel" ||
    entity === "salesperson" ||
    entity === "webpage";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] md:max-w-[1000px] lg:max-w-[1200px] bg-card">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-0 py-4">
          {(entity === "person" ||
            entity === "opportunity" ||
            entity === "campaign" ||
            entity === "channel" ||
            entity === "salesperson" ||
            entity === "webpage") && (
            <FilterSection category="Account" icon={Building2}>
              {(entity === "opportunity" || entity === "person") && (
                <p className="text-xs text-muted-foreground mb-3">
                  Show records related to <strong>any</strong> account that
                  matches <strong>all</strong> specified criteria. Multiple
                  criteria combined with AND logic.
                </p>
              )}
              {(entity === "campaign" ||
                entity === "channel" ||
                entity === "salesperson" ||
                entity === "webpage") && (
                <p className="text-xs text-muted-foreground mb-3">
                  Filter which accounts are included when calculating top-level
                  metrics. Multiple criteria combined with AND logic.
                </p>
              )}
              {loadingAccount ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <FilterBuilder
                  availableFields={accountFields}
                  value={stagedAccountFilters}
                  onChange={setStagedAccountFilters}
                />
              )}
            </FilterSection>
          )}
          {(entity === "account" ||
            entity === "person" ||
            entity === "campaign" ||
            entity === "channel" ||
            entity === "salesperson" ||
            entity === "webpage") && (
            <FilterSection category="Opportunity" icon={Briefcase}>
              {(entity === "account" || entity === "person") && (
                <p className="text-xs text-muted-foreground mb-3">
                  Show records related to <strong>any</strong> opportunity that
                  matches <strong>all</strong> specified criteria. Multiple
                  criteria combined with AND logic.
                </p>
              )}
              {(entity === "campaign" ||
                entity === "channel" ||
                entity === "salesperson" ||
                entity === "webpage") && (
                <p className="text-xs text-muted-foreground mb-3">
                  Filter which opportunities are included when calculating
                  top-level metrics. Multiple criteria combined with AND logic.
                </p>
              )}
              {loadingOpportunity ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <FilterBuilder
                  availableFields={opportunityFields}
                  value={stagedOpportunityFilters}
                  onChange={setStagedOpportunityFilters}
                />
              )}
            </FilterSection>
          )}
          {entity === "channel" && !hideCampaignFilters && (
            <FilterSection category="Campaign" icon={Megaphone}>
              <p className="text-xs text-muted-foreground mb-3">
                Show channels related to <strong>any</strong> campaign that
                matches <strong>all</strong> specified criteria. Multiple
                criteria combined with AND logic.
              </p>
              {loadingCampaign ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <FilterBuilder
                  availableFields={campaignFields}
                  value={stagedCampaignFilters}
                  onChange={setStagedCampaignFilters}
                />
              )}
            </FilterSection>
          )}
          {usesFilterBuilderForTouchpoints && (
            <FilterSection category="Touchpoint" icon={Clock}>
              <p className="text-xs text-muted-foreground mb-3">
                Filter which touchpoints are <strong>counted</strong> in metric
                columns like # Touchpoints and Date of First/Last Touchpoint.
                Multiple criteria combined with AND logic.
              </p>
              {loadingTouchpoint ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <FilterBuilder
                  availableFields={touchpointFields}
                  value={stagedTouchpointFilters}
                  onChange={setStagedTouchpointFilters}
                />
              )}
            </FilterSection>
          )}
          {usesDatePickerForTouchpoints && (
            <FilterSection category="Touchpoint" icon={Clock}>
              <p className="text-xs text-muted-foreground mb-3">
                Filter which touchpoints are <strong>counted</strong> in metric
                columns by timestamp.
              </p>
              <div className="space-y-2">
                <Label>Timestamp Range</Label>
                <div className="flex items-center gap-2">
                  <DatePickerInput
                    value={touchpointDateFrom}
                    onChange={(date) =>
                      handleTouchpointDateChange("from", date)
                    }
                    className="w-44"
                    placeholder="YYYY-MM-DD"
                    maxDate={
                      touchpointDateTo
                        ? safeParseDateString(touchpointDateTo)
                        : undefined
                    }
                  />
                  <span className="text-muted-foreground">to</span>
                  <DatePickerInput
                    value={touchpointDateTo}
                    onChange={(date) => handleTouchpointDateChange("to", date)}
                    className="w-44"
                    placeholder="YYYY-MM-DD"
                    minDate={
                      touchpointDateFrom
                        ? safeParseDateString(touchpointDateFrom)
                        : undefined
                    }
                  />
                </div>
              </div>
            </FilterSection>
          )}
        </div>
        <DialogFooter>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleClearAll}>
              Clear All
            </Button>
            <Button onClick={handleApply}>Apply</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
